const tls = require('tls');
const { ErrorSubcode } = require('solclient-error');
const { Hex } = require('solclient-convert');
const { LOG_TRACE, LOG_DEBUG, LOG_ERROR } = require('solclient-log');
const { mixin } = require('solclient-eskit');
const { NodeTLSOptsMixin } = require('../node-tls-opts-mixin');
const { ShuntableTransport } = require('./shuntable-transport');
const { TransportBase } = require('../transport-base');
const { TransportError } = require('../transport-error');
const { TransportReturnCode } = require('../transport-return-codes');
const { TransportSessionEvent } = require('../transport-session-event');
const { TransportSessionEventCode } = require('../transport-session-event-codes');
const { TransportSessionState } = require('../transport-session-states');

const {
  formatHexString,
} = Hex;

/**
 *
 * Modular transport with TLS (tcps://) support.
 * Only works in node.js.
 * Must be initialized with an underlying transport
 * (currently only the plaintext tcp transport is supported).
 * Wraps the underlying transport in a ShuntableTransport stream
 * which can be passed to tls.connect()
 * Supports  TLS shutdown leaving the socket open.
 * @private
 */
class TlsOnlyTransport extends TransportBase {

  constructor(url, eventCB, client, props) {
    // instanitate instance properties defined in TransportBase
    super(url, eventCB, client, props);

    /**
     * @private
     * @type {TransportClientStats}
     */
    this._clientstats = null;
    this._underlyingTransport = null;

    /**
     * Maximum amount of send data than can be queued.
     * NodeJS socket.write accepts all writes, growing a
     * user space buffer indefinitely.
     * The TCP transport (same as WS transport)
     * manages the size of that buffer to stay below this limit.
     * @private
     * @type {Number}
     */
    this._sendBufferMaxSize = props.sendBufferMaxSize;

    /*
     * When true, the upper layer was told NO_SPACE and must be notified
     * of the next "drained" event.
     * @private
     * @type {Boolean}
     */
    this._canSendNeeded = false;

    /*
     * One-shot flush callback.
     * Set on flush(), ran and cleared on socket drain event.
     * @private
     */
    this._flushCallback = null;

    /**
     * Flag to keep track of socket drained state:
     * Set to false when socket.write returns false,
     * (indicating user-space buffering)
     * Set to true on socket drain events.
     * (indicating the user space buffer has flushed to the OS.)
     * @type {Boolean}
     * @private
     */
    this._drained = true;

    /**
     * @type {TransportSessionState}
     * @private
     */
    this._state = TransportSessionState.DOWN;

    /**
     * @type {?Socket}
     * @private
     */
    this._tlsSocket = null;

    /**
     * Save the Event in the onError listener
     * Use it in onClose to populate the event.
     * (Tcp socket calls onError with the error then onClose.
     * WS socket calls only onClose and passes the error directly.)
     * @private
     * @type {?Error}
     */
    this._socketError = null;

    /**
     * @type {?string}
     * @private
     */
    this._sessionId = new Date().getTime();

    // Some whitebox tests expect a transport->transportSession nesting.
    this._transportSession = this;

    this._props.transportProtocol = null;
  }

  /**
   * @override
   */
  getClientStats() {
    return this._clientstats;
  }

  setClientStats(value) {
    this._clientstats = value;
  }

  setUnderlyingTransport(newUnderlyingTransoprt) {
    this._underlyingTransport = newUnderlyingTransoprt;
  }

  getUnderlyingTransport() {
    return this._underlyingTransport;
  }

  rehome(eventCB, client) {
    this._eventCB = eventCB;
    this._client = client;
  }

  // eventCB and rxDataBuffer forwarded to _shuntableTransport
  // Chicken-egg problem in the factory...
  eventCB(transportEvent) {
    this._shuntableTransport.eventCB(transportEvent);
  }

  rxDataBuffer(data) {
    this._shuntableTransport.rxDataBuffer(data);
  }

  /**
   * Socket callback.
   * Bind the socket on setup to avoid stray errors from old sockets
   * in quick connect()-destroy()-connect() scenarios.
   * @private
   * @param {Socket} originalSocket the original socket
   * @param {Buffer} data the data
   */
  onData(originalSocket, data) {
    // We do not collect Rx stats.
    if (this._state === TransportSessionState.WAITING_FOR_DESTROY ||
      originalSocket !== this._tlsSocket) {
      LOG_DEBUG('TcpTransportSession stray onData for previous socket, ignoring.');
      return;
    }
    if (this._client) {
      this._client.rxDataBuffer(data);
    }
  }

  /**
   * Socket callback.
   * Bind the socket on setup to avoid stray errors from old sockets
   * in quick connect()-destroy()-connect() scenarios.
   * @private
   * @param {Socket} originalSocket the original socket
   * @param {Error} error the error
   */
  onError(originalSocket, error) {
    if (originalSocket !== this._tlsSocket) {
      LOG_DEBUG('Tls transport stray onError for previous socket, ignoring.');
      return;
    }

    if (this._state === TransportSessionState.WAITING_FOR_DESTROY) {
      LOG_TRACE('Tls transport is being destroyed, ignore error');
      return;
    }
    LOG_DEBUG(`Tls transport connection error ${error}`);
    // net.socket invokes the error handler before the close handler.
    // Websocket does not, and that's what the upper layer expects.
    // Suppressing the "double tap" error events when connected,
    // saving the error for the onClose handler in _socketerror.
    if (this._state !== TransportSessionState.SESSION_UP) {
      this._eventCB(
        new TransportSessionEvent(
          TransportSessionEventCode.SEND_ERROR,
          `Connection error${error}`,
          null,
          ErrorSubcode.CONNECTION_ERROR, null));
    } else { // we rely on the close listener to notify the upper layer.
      this._socketError = error;
    }
  }

  /**
   * Socket callback.
   * Bind the socket argument to guard against stray invocations in a
   * quick connect()-destroy()-connect() scenario.
   * @private
   * @param {Socket} originalSocket original socket
   * @param {Boolean} hadError wheter there was an error
   */
  onClose(originalSocket, hadError) {
    if (originalSocket !== this._tlsSocket) {
      LOG_DEBUG('TcpTransportSession stray onClose for previous socket, ignoring.');
      return;
    }

    if (this._state === TransportSessionState.WAITING_FOR_DESTROY) {
      LOG_TRACE('Tls transport is being destroyed, ignore close');
      return;
    }
    const msg = hadError ? 'with error' : 'cleanly';
    const errorMsg = this._socketError && this._socketError.message;
    this._socketError = null; // clear on read.
    LOG_DEBUG(`Tls transport connection is closed ${msg}. Message: ${errorMsg}`);
    this._state = TransportSessionState.CONNECTION_FAILED;
    this._destroy(this._tlsSocket, `Connection closed ${msg}. Message:${errorMsg}`,
                  ErrorSubcode.COMMUNICATION_ERROR);
  }

  /**
   * Callback for Socket.connect.
   * Bind the socket argument to guard against stray invocations in a
   * quick connect()-destroy()-connect() scenario.
   * @private
   * @param {Socket} originalSocket original socket
   */
  onOpen(originalSocket) {
    LOG_TRACE('TlsTransportSession received socket open event.');
    if (originalSocket !== this._tlsSocket) {
      LOG_DEBUG('TlsTransportSession stray onOpen for previous socket, ignoring.');
      return;
    }
    if (this._state !== TransportSessionState.WAITING_FOR_CREATE) {
      LOG_DEBUG(`TcpTransportSession stray onOpen when in state ${this._state}, ignoring.`);
      return;
    }

    this._state = TransportSessionState.SESSION_UP;
      // Send the event to the application letting it know that the session is up
    this._eventCB(
          new TransportSessionEvent(
              TransportSessionEventCode.UP_NOTICE,
              'Connected',
              0,
              null,
              this._sessionId));
  }

  /**
   * Establish underlying transport.
   * @override
   * @returns {TransportReturnCode} The result of this operation
   */
  connect() {
    if (this._state !== TransportSessionState.DOWN) {
      LOG_ERROR(`Invalid state for operation: ${TransportSessionState.nameOf(this._state)}`);
      return TransportReturnCode.INVALID_STATE_FOR_OPERATION;
    }
    if (this._tlsSocket) {
      LOG_ERROR('TCP transport can not connect once _tlsSocket exists.');
      return TransportReturnCode.INVALID_STATE_FOR_OPERATION;
    }

    LOG_DEBUG('Establishing TLS transport session');
    this._state = TransportSessionState.WAITING_FOR_CREATE;
    try {
      const duplexOptions = { underlyingTransport: this._underlyingTransport };
      this._shuntableTransport = new ShuntableTransport(duplexOptions);
      this._underlyingTransport.connect();
      const tlsOptions = this.createTLSOptions();
      tlsOptions.socket = this._shuntableTransport;
      // host and port can be set here for certificate validation in the future.
      this._tlsSocket = tls.connect(tlsOptions);
      this._tlsSocket.on('secureConnect', this.onOpen.bind(this, this._tlsSocket));
      LOG_TRACE('Assigning properties to socket');
      // WARNING!
      // This is the kind of thing that breaks on minification.
      // Re-run unit tests in production mode when changed:
      // grunt --gruntfile=Gruntfile_Production.js
      // When broken, consult the closure compiler exceptions file:
      // (sdk-root)/builder/template/-common/env/externs.js
      this._tlsSocket.on('data', this.onData.bind(this, this._tlsSocket));
      this._tlsSocket.on('error', this.onError.bind(this, this._tlsSocket));
      this._tlsSocket.on('close', this.onClose.bind(this, this._tlsSocket));
      this._tlsSocket.on('drain', this.onDrain.bind(this, this._tlsSocket));
      this._tlsSocket.setNoDelay(true);

      LOG_TRACE('Prepared socket');
    } catch (error) {
      LOG_DEBUG(`Error connecting: ${error}`);
      this._state = TransportSessionState.CONNECTION_FAILED;
      throw new TransportError(`Could not create TLS socket: ${error.message}`,
        error.subcode || ErrorSubcode.CONNECTION_ERROR);
    }
    LOG_DEBUG('TLS transport is connecting');

    return TransportReturnCode.OK;
  }

  /**
   * Destroy the underlying transport.
   * Returns transport return code.
   * @override
   * @param {?String} msg The message associated with this operation, if any.
   * @param {?ErrorSubcode} subcode The subcode associated with this operation, if any.
   * @returns {TransportReturnCode} The result of this operation
   */
  destroy(msg, subcode) {
    //return this._underlyingTransport.destroy(msg, subcode);
    return this._destroy(this._tlsSocket, msg, subcode);
  }

  _destroy(originalSocket, msg, subcode) {
    if (originalSocket !== this._tlsSocket) {
      LOG_DEBUG('TcpTransportSession stray _destroy for previous socket, ignoring.');
      return TransportReturnCode.OK;
    }
    if (this._state !== TransportSessionState.DOWN) {
      LOG_DEBUG(`Destroy Tls transport: ${msg}`);
      // Set state for connection teardown.
      this._state = TransportSessionState.WAITING_FOR_DESTROY;

      const onerrorStub = function onerrorStub() { };
      if (this._tlsSocket) {
        this._tlsSocket.removeAllListeners();
        this._tlsSocket.on('error', onerrorStub); // Prevent unhandled errors
        this._tlsSocket.end();
        this._tlsSocket = null;
      }

      if (this._underlyingTransport) {
        this._underlyingTransport.destroy(msg, subcode);
        this._underlyingTransport = null;
      }

      this._canSendNeeded = false;
      this._flushCallback = null;
      this._drained = true;
      this._state = TransportSessionState.DOWN;
      this._client = null; // Don't accept data.
    }

    // always send a DESTROYED_NOTICE in response to a destroy() even
    // if we think the transport is already destroyed
    LOG_TRACE(`Destroying TCP transport. msg: ${msg} subcode: ${subcode}`);
    if (this._eventCB) {
      this._eventCB(new TransportSessionEvent(TransportSessionEventCode.DESTROYED_NOTICE,
                                              msg || 'Session is destroyed',
                                              null,
                                              subcode || 0,
                                              this._sessionId));
      // Release references to other components
      this._eventCB = null;
    }
    return TransportReturnCode.OK;
  }

  /**
   * Force fail the underlying transport's socket to force a failure.
   * Returns transport return code.
   * @override
   * @param {?String} msg The message associated with this operation, if any.
   * @returns {TransportReturnCode} The result of this operation
   */
  forceFailure(msg) {
    if (this._state !== TransportSessionState.DOWN) {
      const errorMsg = (msg !== undefined && msg !== null) ? msg : ''; // default empty
      LOG_DEBUG(`Destroy Tls transport: ${errorMsg}`);
      if (this._tlsSocket) {
        this._tlsSocket.destroy(new Error(errorMsg));
      }

      if (this._underlyingTransport) {
        this._underlyingTransport.forceFailure(errorMsg);
      }
    }
    return TransportReturnCode.OK;
  }

  /**
   * @override
   * @description Intended for clean shutdowns.
   * Callback is invoked when all nodejs-buffered data
   * has been flushed to the OS socket.
   */
  flush(callback) {
    if (this._drained) {
      LOG_DEBUG('Transport emitting FLUSH immediately.');
      callback();
    } else if (callback) {
      this._flushCallback = callback;
    }
    return TransportReturnCode.OK;
  }

  /**
   * @override
   * @description Send encoded SMF message
   * Returns transport return code.
   * @param {String} message The encoded SMF message to send
   * @param {?Boolean} [forceAllowEnqueue=false] If true, do not fail due to transport buffer full
   * @returns {TransportReturnCode} The result of this operation
   */
  send(message, forceAllowEnqueue = false) {
    if (this._state !== TransportSessionState.SESSION_UP) {
      return TransportReturnCode.INVALID_STATE_FOR_OPERATION;
    }
    // Limiting socket buffer size to _sendBufferMaxSize unless
    // the force flag was set
    // or the socket is drained, in which case any size message goes.
    if (forceAllowEnqueue
      || this._drained
      || message.length < this._sendBufferMaxSize - this._tlsSocket.bufferSize) {
      // _drained keeps track of "overflowing" writes.
      // nodejs never refuses a write but warns when it starts buffering.
      this._drained = this._tlsSocket.write(message, 'ascii');
      if (this._clientstats) {
        this._clientstats.bytesWritten += message.length;
        ++this._clientstats.msgWritten;
      }
      return TransportReturnCode.OK;
    }
    // We'll unblock the user on the next onDrain.
    this._canSendNeeded = true;
    return TransportReturnCode.NO_SPACE;
  }

  /**
   * Socket callback.
   * Bind the socket on setup to avoid stray errors from old sockets
   * in quick connect()-destry()-connect() scenarios.
   * @private
   * @param {Socket} originalSocket the original socket
   */
  onDrain(originalSocket) {
    if (originalSocket !== this._tlsSocket) {
      LOG_DEBUG('TcpTransportSession stray onDrain for previous socket, ignoring.');
      return;
    }
    this._drained = true;

    if (this._flushCallback) {
      LOG_DEBUG('Transport emitting FLUSH after drain.');
      const prevCallback = this._flushCallback;
      this._flushCallback = null;
      prevCallback();
    }

    if (this._canSendNeeded) {
      LOG_TRACE('Transport emitting CAN_ACCEPT_DATA');
      this._canSendNeeded = false;
      this._eventCB(
        new TransportSessionEvent(TransportSessionEventCode.CAN_ACCEPT_DATA,
                                  '', null, 0, this._sessionId));
    }
  }

  /**
   * @override
   */
  getInfoStr() {
    const str = `TcpTransportSession; sid=${formatHexString(this._sessionId)}`;
    return str;
  }

  tlsShutdown(callback) {
    this._tlsSocket.removeAllListeners('close');
    this._tlsSocket.on('close', this.tlsShutdownStage2.bind(this, callback));
    this._shuntableTransport.dontEnd();
    this._tlsSocket.end();
  }

  tlsShutdownStage2(callback) {
    this._shuntableTransport.shunt();
    this._tlsSocket.removeAllListeners();
    this._tlsSocket = null;
    this._shuntableTransport = null;
    this._underlyingTransport.rehome(this._eventCB, this._client);
    this._underlyingTransport.setClientStats(this.getClientStats());
    this.setClientStats(null);
    this._eventCB = null;
    this._client = null;
    callback(this._underlyingTransport);
  }
}

mixin(TlsOnlyTransport, NodeTLSOptsMixin);

module.exports.TlsOnlyTransport = TlsOnlyTransport;
